import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PERMISSIONS, PRODUCT_STATUSES } from '@solar/contracts';

import {
  AdminIcon,
  ManagementHeader,
  MetricCard,
  adminManagementStyles as styles,
} from '../../../components/admin/AdminManagement';
import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { useAuth } from '../../../features/auth/auth-context';
import { catalogueApi } from '../../../features/catalogue/catalogue.api';
import { operationalApi } from '../../../features/reporting/operational.api';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatNgn } from '../../../lib/format-currency';
import { formatStatusLabel } from '../../../lib/format-status';
import { paths, routeTo } from '../../../routes/paths';

export function AdminProductsPage(): React.JSX.Element {
  const { can } = useAuth();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [appliedCategory, setAppliedCategory] = useState('');
  const products = useQuery({
    queryKey: ['admin', 'products', { page, status, category: appliedCategory }],
    queryFn: () =>
      catalogueApi.listAdminProducts({
        page,
        pageSize: 20,
        status: status || undefined,
        category: appliedCategory || undefined,
      }),
  });
  const stats = useQuery({
    queryKey: ['admin', 'products', 'stats'],
    queryFn: () => operationalApi.productStats(),
  });
  const items = products.data?.items ?? [];
  const active = stats.data?.byStatus.find((item) => item.status === 'ACTIVE')?.count;

  return (
    <div className={styles.page}>
      <ManagementHeader
        eyebrow="Catalogue management"
        title="Products"
        subtitle="Manage the solar systems customers can apply for."
        icon="package"
        context="Product catalogue"
        action={
          can(PERMISSIONS.PLANS_MANAGE) ? (
            <Link className={styles.buttonLink} to={paths.adminProductCreate}>
              Create product <AdminIcon name="arrow" />
            </Link>
          ) : undefined
        }
      />
      <section className={styles.hero}>
        <div>
          <h2>Build a clear, application-ready catalogue</h2>
          <p>Availability and commercial details remain controlled by the catalogue service.</p>
        </div>
        <strong>
          {stats.data
            ? `${stats.data.totalProducts} ${stats.data.totalProducts === 1 ? 'product' : 'products'}`
            : 'Catalogue'}
        </strong>
      </section>
      <section className={styles.metrics} aria-label="Product summary">
        <MetricCard
          label="Total products"
          value={stats.data?.totalProducts ?? '—'}
          icon="package"
        />
        <MetricCard label="Active products" value={active ?? '—'} icon="check" tone="blue" />
        <MetricCard
          label="Linked plans"
          value={stats.data?.linkedPlanTotal ?? '—'}
          icon="plan"
          tone="amber"
        />
        <MetricCard
          label="Categories"
          value={stats.data?.byCategory.length ?? '—'}
          icon="filter"
          tone="violet"
        />
      </section>
      <form
        className={styles.filterPanel}
        onSubmit={(event) => {
          event.preventDefault();
          setAppliedCategory(category.trim());
          setPage(1);
        }}
      >
        <label>
          Category
          <input
            value={category}
            placeholder="Filter by category"
            onChange={(event) => setCategory(event.target.value)}
          />
        </label>
        <label>
          Status
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
          >
            <option value="">All statuses</option>
            {PRODUCT_STATUSES.map((value) => (
              <option value={value} key={value}>
                {formatStatusLabel(value)}
              </option>
            ))}
          </select>
        </label>
        <button type="submit">
          <AdminIcon name="filter" /> Apply filters
        </button>
      </form>
      <div className={styles.contentGrid}>
        <section className={styles.mainCard}>
          <header className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <span className={styles.sectionIcon}>
                <AdminIcon name="package" />
              </span>
              <div>
                <h2>Solar products</h2>
                <p>Pricing, availability and live usage</p>
              </div>
            </div>
            {products.data && (
              <span className={styles.countBadge}>
                {products.data.pagination.totalItems}{' '}
                {products.data.pagination.totalItems === 1 ? 'product' : 'products'}
              </span>
            )}
          </header>
          {products.isPending && <p className={styles.empty}>Loading products…</p>}
          {products.isError && (
            <div className={styles.alertWrap}>
              <Alert tone="error">{toDisplayMessage(products.error)}</Alert>
            </div>
          )}
          {products.isSuccess && items.length === 0 && (
            <p className={styles.empty}>No products match the current filters.</p>
          )}
          {items.length > 0 && (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Capacity</th>
                    <th>Base price</th>
                    <th>Status</th>
                    <th>Availability</th>
                    <th>Components</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((product) => (
                    <tr key={product.id}>
                      <td>
                        <div className={styles.primaryCell}>
                          <span className={styles.avatar}>
                            <AdminIcon name="spark" />
                          </span>
                          <span>
                            <strong>{product.name}</strong>
                            <small>{product.code}</small>
                          </span>
                        </div>
                      </td>
                      <td>{product.category}</td>
                      <td>{product.systemCapacityKw} kW</td>
                      <td>{formatNgn(product.basePrice)}</td>
                      <td>
                        <span
                          className={`${styles.status} ${product.status === 'ACTIVE' ? styles.success : styles.warning}`}
                        >
                          {formatStatusLabel(product.status)}
                        </span>
                      </td>
                      <td>{product.isAvailable === 'true' ? 'Available' : 'Unavailable'}</td>
                      <td>
                        {product.components === undefined
                          ? 'Open product'
                          : product.components.length === 0
                            ? 'Needs setup'
                            : `${product.components.length} configured`}
                      </td>
                      <td>
                        <Link to={routeTo.adminProduct(product.id)}>Manage →</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {products.data && products.data.pagination.totalPages > 1 && (
            <nav className={styles.pagination}>
              <Button
                variant="secondary"
                disabled={!products.data.pagination.hasPreviousPage}
                onClick={() => setPage((value) => value - 1)}
              >
                Previous
              </Button>
              <span>
                Page {products.data.pagination.page} of {products.data.pagination.totalPages}
              </span>
              <Button
                variant="secondary"
                disabled={!products.data.pagination.hasNextPage}
                onClick={() => setPage((value) => value + 1)}
              >
                Next
              </Button>
            </nav>
          )}
        </section>
        <aside className={styles.rail}>
          <section className={styles.railCard}>
            <h2>Product mix</h2>
            <p>Authoritative category distribution.</p>
            <ul className={styles.railList}>
              {stats.data?.byCategory.map((item) => (
                <li key={item.category}>
                  <span>
                    <i className={styles.dot} />
                    {item.category}
                  </span>
                  <strong>{item.count}</strong>
                </li>
              ))}
            </ul>
          </section>
          <section className={styles.railCard}>
            <h2>Catalogue guidance</h2>
            <p>
              Keep capacity, price, components and warranty information complete before making a
              product available.
            </p>
          </section>
        </aside>
      </div>
    </div>
  );
}
